import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Quiz, QuizInsert, QuizUpdate } from '@/types/quiz';
import { toast } from 'sonner';

export const useQuizzes = (brandId?: string) => {
  return useQuery({
    queryKey: ['quizzes', brandId],
    queryFn: async () => {
      let query = supabase
        .from('os_quizzes')
        .select(`
          *, 
          os_quiz_types(name, display_name, icon), 
          os_quiz_styles(name),
          os_quiz_templates!os_quizzes_template_id_fkey(
            id,
            name,
            os_quiz_types(name, display_name, icon)
          )
        `)
        .order('created_at', { ascending: false });

      if (brandId) {
        query = query.eq('brand_id', brandId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
    enabled: !!brandId,
  });
};

export const useQuiz = (quizId?: string) => {
  return useQuery({
    queryKey: ['quiz', quizId],
    queryFn: async () => {
      if (!quizId) throw new Error('Quiz ID is required');

      const { data, error } = await supabase
        .from('os_quizzes')
        .select(`
          *, 
          os_quiz_types(name, display_name, icon), 
          os_quiz_styles(name, style_config, theme_config),
          os_quiz_templates!os_quizzes_template_id_fkey(
            id,
            name,
            section_configs,
            os_quiz_types(name, display_name, icon)
          )
        `)
        .eq('id', quizId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!quizId,
  });
};

export const useQuizByShareCode = (shareCode?: string) => {
  return useQuery({
    queryKey: ['quiz-by-share-code', shareCode],
    queryFn: async () => {
      if (!shareCode) throw new Error('Share code is required');

      const { data, error } = await supabase
        .from('os_quizzes')
        .select(`
          *, 
          os_quiz_types(name, display_name, icon), 
          os_quiz_styles(name, style_config, theme_config),
          os_quiz_templates!os_quizzes_template_id_fkey(
            id,
            name,
            section_configs,
            os_quiz_types(name, display_name, icon)
          )
        `)
        .eq('share_code', shareCode)
        .eq('is_published', true)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Quiz not found or not published');
      if (!data.brand_id) throw new Error('Quiz missing brand information');
      return data;
    },
    enabled: !!shareCode,
  });
};

export const useCreateQuiz = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quiz: QuizInsert) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('os_quizzes')
        .insert({
          ...quiz,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Quiz;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes', data.brand_id] });
      toast.success('Quiz created successfully');
    },
    onError: (error) => {
      toast.error(`Failed to create quiz: ${error.message}`);
    },
  });
};

export const useUpdateQuiz = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: QuizUpdate }) => {
      const { data, error } = await supabase
        .from('os_quizzes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Quiz;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['quiz', data.id] });
      if (data.share_code) {
        queryClient.invalidateQueries({ queryKey: ['quiz-by-share-code', data.share_code] });
      }
      toast.success('Quiz updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update quiz: ${error.message}`);
    },
  });
};

export const useDeleteQuiz = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_quizzes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quizzes'] });
      toast.success('Quiz deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete quiz: ${error.message}`);
    },
  });
};

export const usePublishQuiz = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Generate share code if not exists
      const { data: quiz } = await supabase
        .from('os_quizzes')
        .select('share_code')
        .eq('id', id)
        .single();

      let shareCode = quiz?.share_code;
      
      if (!shareCode) {
        const { data: codeData } = await supabase.rpc('generate_quiz_share_code');
        shareCode = codeData;
      }

      const { data, error } = await supabase
        .from('os_quizzes')
        .update({
          is_published: true,
          share_code: shareCode,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Quiz;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['quiz', data.id] });
      toast.success('Quiz published successfully');
    },
    onError: (error) => {
      toast.error(`Failed to publish quiz: ${error.message}`);
    },
  });
};

export const useUnpublishQuiz = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('os_quizzes')
        .update({ is_published: false })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Quiz;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quizzes', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['quiz', data.id] });
      toast.success('Quiz unpublished successfully');
    },
    onError: (error) => {
      toast.error(`Failed to unpublish quiz: ${error.message}`);
    },
  });
};
